import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setBaseView } from "@store/slices/viewSlice";
import {
  selectIsSeedingSample,
  selectIsTourRunning,
  selectOnboardingState,
  selectTourStartAt,
  setOnboardingState,
  startTour,
  stopTour,
} from "../state/onboardingSlice";
import { getOnboardingState } from "@services/api";
import { useOnboardingSave } from "../state/useOnboardingSave";
import {
  ADD_READING_PROMPT_KEY,
  ADD_READING_PROMPT_VERSION,
  SAMPLE_SEED_KEY,
  SAMPLE_SEED_VERSION,
  TOUR_FLOW_KEY,
  TOUR_VERSION,
  WELCOME_KEY,
  WELCOME_VERSION,
} from "../tourSteps";
import WelcomeDialog from "./WelcomeDialog";
import { seedSampleContent } from "../state/seedSampleContent";
import { setSeeding } from "../state/onboardingSlice";
import type { TourStepHelpers } from "../tourSteps";
import type { OnboardingEntryJSON } from "@/types/models";
import {
  selectIsCurrentUserWorkspaceOwner,
  selectReadings,
} from "@store/selectors";
import FirstReadingPrompt from "./FirstReadingPrompt";

// Lazy boundary for the guided tour: react-joyride is only fetched
// when a tour actually runs (first-run or "Replay tour"), which most
// sessions never trigger. Import Tour (and through it, react-joyride)
// nowhere else — a static import collapses the code split.
const Tour = lazy(() => import("./Tour"));

interface Props {
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** True once a reading/canvas is on screen — the tour never fires
   * over an empty studio. */
  contentReady: boolean;
}

const isDone = (entry: OnboardingEntryJSON | undefined, version: number) =>
  !!entry &&
  entry.version >= version &&
  !!(entry.completed_at || entry.dismissed_at);

export default function TourHost({
  isSidebarOpen,
  setSidebarOpen,
  contentReady,
}: Props) {
  const dispatch = useAppDispatch();
  const run = useAppSelector(selectIsTourRunning);
  const startAt = useAppSelector(selectTourStartAt);
  const entries = useAppSelector(selectOnboardingState);
  const userId = useAppSelector((s) => s.user.userId);
  const baseView = useAppSelector((s) => s.view.baseView);

  // The tour is a layer, not a destination: its preconditions drive
  // the app (switch to canvas, open the sidebar), so snapshot the view
  // state when a tour starts and put everything back when it ends —
  // whoever entered on the reading leaves on the reading.
  const preTourView = useRef<{
    baseView: typeof baseView;
    sidebarOpen: boolean;
  } | null>(null);
  useEffect(() => {
    if (run && !preTourView.current) {
      preTourView.current = { baseView, sidebarOpen: isSidebarOpen };
    } else if (!run && preTourView.current) {
      const previous = preTourView.current;
      preTourView.current = null;
      dispatch(setBaseView(previous.baseView));
      setSidebarOpen(previous.sidebarOpen);
    }
  }, [run, baseView, isSidebarOpen, dispatch, setSidebarOpen]);

  // Signed-in users: the server copy is authoritative — pull it before
  // deciding whether to auto-fire. Anonymous/demo state is already in
  // the store (IndexedDB hydrates before the tree renders).
  const [stateReady, setStateReady] = useState(!userId);
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setStateReady(true);
      return;
    }
    setStateReady(false);

    // Every onboarding surface waits on this flag, so a failed read must
    // never leave it false — that would silently disable seeding, the
    // prompt and the tour with no visible error. Retry briefly (a signup
    // can race the lazy creation of the app-DB user row), then continue
    // from a clean slate: a signed-in session must not inherit the
    // anonymous/demo state sitting in IndexedDB.
    const load = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await getOnboardingState();
        if (cancelled) return;
        if (result.success) {
          dispatch(setOnboardingState(result.data.onboarding_state ?? {}));
          setStateReady(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
        if (cancelled) return;
      }
      dispatch(setOnboardingState({}));
      setStateReady(true);
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  // Resume only — never the first offer. A tour the user opted into and
  // was interrupted mid-way through picks up where it left off; a tour
  // they have not been offered yet waits for the welcome dialog below.
  const autoFired = useRef(false);
  useEffect(() => {
    if (autoFired.current || run || !contentReady || !stateReady) return;
    const entry = entries[TOUR_FLOW_KEY];
    if (!entry || typeof entry.step !== "number") return;
    if (isDone(entry, TOUR_VERSION)) return;
    autoFired.current = true;
    dispatch(startTour(entry.version === TOUR_VERSION ? entry.step : 0));
  }, [contentReady, stateReady, entries, run, dispatch]);

  const save = useOnboardingSave();

  const readings = useAppSelector(selectReadings);
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const canvases = useAppSelector((s) => s.workspace.canvases);
  const seeding = useAppSelector(selectIsSeedingSample);

  // Seed sample content for a brand-new account, once ever. Without
  // it the tour spotlights an empty sidebar and an empty canvas —
  // it teaches nothing on the very first run it exists for.
  //
  // "Brand new" is: workspace data has loaded (every workspace is
  // created with a Main Canvas, so a canvas present means the fetch
  // returned), no readings, and no onboarding history at all. That
  // last clause keeps an established user who deleted everything from
  // having sample content reappear.
  const seedAttempted = useRef(false);
  useEffect(() => {
    if (seedAttempted.current || !userId || !stateReady || !isOwner) return;
    if (canvases.length === 0 || readings.length > 0) return;
    if (entries[SAMPLE_SEED_KEY] || entries[TOUR_FLOW_KEY]) return;
    seedAttempted.current = true;
    // Claim the flag before seeding so a second tab (or a reload
    // mid-seed) can't produce a duplicate set.
    save(SAMPLE_SEED_KEY, {
      version: SAMPLE_SEED_VERSION,
      completed_at: new Date().toISOString(),
    });
    dispatch(setSeeding(true));
    dispatch(seedSampleContent()).finally(() => {
      dispatch(setSeeding(false));
    });
  }, [
    userId,
    stateReady,
    isOwner,
    canvases.length,
    readings.length,
    entries,
    dispatch,
    save,
  ]);

  // First-run offer. Shown once the workspace context exists — not once
  // content does — so it greets the user immediately instead of landing
  // seconds later, after sample content has popped in behind it.
  const welcomeSettled =
    isDone(entries[WELCOME_KEY], WELCOME_VERSION) ||
    isDone(entries[TOUR_FLOW_KEY], TOUR_VERSION) ||
    typeof entries[TOUR_FLOW_KEY]?.step === "number";
  const [tourRequested, setTourRequested] = useState(false);
  // Only offer a tour when there is — or imminently will be — something
  // to point at. Gating on seeding rather than on "content not ready
  // yet" matters: if seeding fails there is nothing coming, and a
  // permanently disabled button would strand the user with no way
  // forward but Skip. Here the offer simply withdraws and the
  // add-reading prompt below takes over.
  const showWelcome =
    stateReady && !run && !welcomeSettled && (contentReady || seeding);

  // Honour the request as soon as there is something to point at, so
  // "Take the tour" during seeding starts the moment the sample lands.
  useEffect(() => {
    if (!tourRequested) return;
    if (contentReady) {
      setTourRequested(false);
      dispatch(startTour(0));
    } else if (!seeding) {
      // Nothing arrived — drop the request rather than waiting forever.
      setTourRequested(false);
    }
  }, [tourRequested, contentReady, seeding, dispatch]);

  const settleWelcome = useCallback(() => {
    save(WELCOME_KEY, {
      version: WELCOME_VERSION,
      dismissed_at: new Date().toISOString(),
    });
  }, [save]);

  // Onboarding stage 1: the fallback when there is still nothing to
  // point at (seeding failed, or the user cleared their workspace) —
  // a single coach mark on the navbar's add-reading control. Owners
  // only; contributors can't create readings.
  const showAddReadingPrompt =
    !!userId &&
    stateReady &&
    !contentReady &&
    !run &&
    !seeding &&
    isOwner &&
    readings.length === 0 &&
    !isDone(entries[TOUR_FLOW_KEY], TOUR_VERSION) &&
    !isDone(entries[ADD_READING_PROMPT_KEY], ADD_READING_PROMPT_VERSION);

  // Step progress and completion share one entry, so writing progress
  // naively would wipe an existing completion mark — and a replay that
  // the user abandons (closed tab, navigated away) would leave the tour
  // looking unfinished and fire it at them again on their next visit.
  // Carry the mark forward, but only for the same tour version: after a
  // version bump the old completion must not silence the new tour.
  const stepEntry = useCallback(
    (index: number): OnboardingEntryJSON => {
      const existing = entries[TOUR_FLOW_KEY];
      const sameVersion = existing?.version === TOUR_VERSION;
      return {
        version: TOUR_VERSION,
        step: index,
        ...(sameVersion && existing?.completed_at
          ? { completed_at: existing.completed_at }
          : {}),
        ...(sameVersion && existing?.dismissed_at
          ? { dismissed_at: existing.dismissed_at }
          : {}),
      };
    },
    [entries]
  );

  const helpers: TourStepHelpers = useMemo(
    () => ({
      openSidebar: () => setSidebarOpen(true),
      setBaseView: (view) => dispatch(setBaseView(view)),
    }),
    [dispatch, setSidebarOpen]
  );

  return (
    <>
      <WelcomeDialog
        open={showWelcome || (tourRequested && seeding)}
        preparing={seeding}
        onTakeTour={() => {
          settleWelcome();
          setTourRequested(true);
        }}
        onSkip={() => {
          settleWelcome();
          setTourRequested(false);
        }}
      />
      {showAddReadingPrompt && (
        <FirstReadingPrompt
          onDismiss={() =>
            save(ADD_READING_PROMPT_KEY, {
              version: ADD_READING_PROMPT_VERSION,
              dismissed_at: new Date().toISOString(),
            })
          }
        />
      )}
      {run && (
        <Suspense fallback={null}>
          <Tour
            helpers={helpers}
            initialStepIndex={startAt}
            onStepChange={(index) => save(TOUR_FLOW_KEY, stepEntry(index))}
            onFinish={() =>
              save(TOUR_FLOW_KEY, {
                version: TOUR_VERSION,
                completed_at: new Date().toISOString(),
              })
            }
            onSkip={() =>
              save(TOUR_FLOW_KEY, {
                version: TOUR_VERSION,
                dismissed_at: new Date().toISOString(),
              })
            }
            onClose={() => dispatch(stopTour())}
          />
        </Suspense>
      )}
    </>
  );
}
